import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MapPin, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ThemeToggle } from '@/components/shell/ThemeToggle';
import { BrandLogo } from '@/components/brand/BrandLogo';

/**
 * Marketing-shell legal/contact pages. We render all three (Privacy, Terms,
 * Contact) from a single file so the footer links resolve to a real,
 * branded page each time — no broken `href="#"` placeholders, no 404s.
 *
 * Content is intentionally honest and concise: this is a beta product run
 * by NK Advisory. We do NOT pretend to be a regulated entity or
 * store sensitive card data — the policy reflects exactly what the
 * implementation does.
 */

const KIND_META = {
  privacy: {
    title: 'Privacy Policy',
    eyebrow: 'Last updated: November 2025',
    intro:
      'CardAssist Online is operated by NK Advisory. This policy explains what we collect, why, and how to delete it. Plain English, no dark patterns.',
  },
  terms: {
    title: 'Terms of Service',
    eyebrow: 'Effective: November 2025',
    intro:
      'These terms govern your use of CardAssist Online. By signing up you agree to them. We keep these short and human — none of the all-caps boilerplate.',
  },
  contact: {
    title: 'Contact',
    eyebrow: 'We read every message',
    intro:
      'Bug report, feature suggestion, or partnership question? Reach out — a human reads every email and we aim to reply within two business days.',
  },
};

const PRIVACY_SECTIONS = [
  {
    h: 'What we collect',
    body: (
      <>
        <p>
          When you sign up we store your name, email, and (for password
          accounts) a salted+hashed password. If you sign in with Google we
          store your Google profile name and picture URL — we never see your
          password.
        </p>
        <p>
          Once you start using the product we store:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
          <li>Which cards from our catalog you've added to your wallet.</li>
          <li>Transactions you log (amount, merchant, category, MCC, mode, the card used). <strong>We never store card numbers.</strong></li>
          <li>Goals, milestone progress, leakage events you've dismissed.</li>
          <li>If you install the Chrome extension: anonymised telemetry about cart totals and which routing hint we surfaced (no merchant secrets, no PII).</li>
        </ul>
      </>
    ),
  },
  {
    h: 'How we use it',
    body: (
      <>
        <p>
          Strictly to power CardAssist Online for you: ranking cards, computing
          rewards, surfacing milestone progress, and producing leakage
          insights. We don't sell or share any of it.
        </p>
        <p>
          We use OpenAI for the AI assistant feature only when you explicitly
          chat with it; in that case your message is sent to OpenAI under
          their data-processing terms. The rest of the product runs on
          deterministic math — no AI inference required.
        </p>
      </>
    ),
  },
  {
    h: 'Deleting your data',
    body: (
      <p>
        You can delete individual transactions and milestones from the app at
        any time. To delete your entire account, email{' '}
        <a className="text-primary hover:underline" href="mailto:info@cardassist.online">info@cardassist.online</a> from the email
        address on the account — we'll permanently wipe it within 7 days and
        send you a confirmation.
      </p>
    ),
  },
  {
    h: 'Security',
    body: (
      <p>
        Passwords are hashed with bcrypt. Sessions are bearer JWTs scoped to
        your account. We host on hardened cloud infrastructure with TLS in
        transit. As of beta we recommend not storing anything you wouldn't
        comfortably keep in a spreadsheet.
      </p>
    ),
  },
];

const TERMS_SECTIONS = [
  {
    h: 'Beta service',
    body: (
      <p>
        CardAssist Online is currently in beta. Recommendations are
        informational — final reward calculation is at the discretion of your
        card issuer. Verify any large transaction against your issuer's
        official terms before relying on it.
      </p>
    ),
  },
  {
    h: 'Acceptable use',
    body: (
      <>
        <p>
          You agree to use the product personally and lawfully. Specifically,
          don't:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
          <li>Scrape or reverse-engineer the canonical card library.</li>
          <li>Attempt to harm other users or the service infrastructure.</li>
          <li>Upload PDFs or content you don't have rights to share.</li>
        </ul>
      </>
    ),
  },
  {
    h: 'Liability',
    body: (
      <p>
        NK Advisory provides CardAssist Online "as is" during
        beta. We're not liable for reward calculations made by issuers or
        merchants. If something looks wrong, please report it — we'll fix the
        rule.
      </p>
    ),
  },
  {
    h: 'Changes',
    body: (
      <p>
        If we materially change these terms we'll email you. Continued use of
        the product after notification counts as acceptance.
      </p>
    ),
  },
];

const Section = ({ h, body }) => (
  <section className="mt-8 first:mt-0">
    <h2 className="font-display text-xl font-semibold tracking-tight">{h}</h2>
    <div className="mt-2 space-y-3 text-sm text-foreground/85 leading-relaxed">
      {body}
    </div>
  </section>
);

const ContactBody = () => (
  <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 mt-2">
    <Card className="p-5">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Mail className="h-4 w-4 text-primary" /> Email
      </div>
      <a
        className="mt-2 block font-display text-lg text-foreground hover:text-primary"
        href="mailto:info@cardassist.online"
        data-testid="contact-email-link"
      >
        info@cardassist.online
      </a>
      <p className="mt-1 text-xs text-muted-foreground">Product feedback, bug reports, account help.</p>
    </Card>
    <Card className="p-5">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Building2 className="h-4 w-4 text-primary" /> Legal entity
      </div>
      <div className="mt-2 font-display text-lg text-foreground">NK Advisory</div>
      <div className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
        <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
        <span>India.</span>
      </div>
    </Card>
  </div>
);

export default function Legal({ kind = 'privacy' }) {
  const navigate = useNavigate();
  const loc = useLocation();
  // Allow the file to render based on route too — keeps the prop optional.
  const resolved = ['privacy', 'terms', 'contact'].includes(kind) ? kind : 'privacy';
  const meta = KIND_META[resolved];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid={`legal-page-${resolved}`}>
      <header className="sticky top-0 z-30 bg-[hsl(var(--background))]/85 backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="legal-logo-link">
            <BrandLogo height={40} testId="legal-brand" />
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(-1)}
              className="gap-1.5"
              data-testid="legal-back-button"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="text-xs uppercase tracking-[0.18em] text-primary font-medium" data-testid="legal-eyebrow">
          {meta.eyebrow}
        </div>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="legal-title">
          {meta.title}
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">{meta.intro}</p>

        <div className="mt-8">
          {resolved === 'privacy' && PRIVACY_SECTIONS.map((s) => <Section key={s.h} {...s} />)}
          {resolved === 'terms' && TERMS_SECTIONS.map((s) => <Section key={s.h} {...s} />)}
          {resolved === 'contact' && <ContactBody />}
        </div>

        {/* Cross-links between policies */}
        <div className="mt-12 pt-6 border-t border-border flex flex-wrap items-center gap-4 text-sm">
          <span className="text-muted-foreground">More:</span>
          {[
            { to: '/privacy', label: 'Privacy', id: 'privacy' },
            { to: '/terms', label: 'Terms', id: 'terms' },
            { to: '/contact', label: 'Contact', id: 'contact' },
          ]
            .filter((l) => l.id !== resolved && loc.pathname !== l.to)
            .map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-primary hover:underline"
                data-testid={`legal-cross-link-${l.id}`}
              >
                {l.label}
              </Link>
            ))}
        </div>
      </main>

      <footer className="border-t border-border py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-xs text-muted-foreground flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <span>© {new Date().getFullYear()} CardAssist Online · NK Advisory</span>
          <Link to="/" className="hover:text-foreground">Back to home</Link>
        </div>
      </footer>
    </div>
  );
}
